import { type FC, type KeyboardEvent, useCallback } from 'react';

import cn from 'classnames';

import { Avatar } from '../ui/Avatar';
import { PillButton } from '../ui/PillButton';

import { composerTriggerAppearances } from './composerStyles';

export type ComposerTriggerAppearance = keyof typeof composerTriggerAppearances;

interface ComposerTriggerProps {
  avatarUrl?: string;
  avatarAlt?: string;
  placeholder: string;
  buttonLabel: string;
  onClick: () => void;
  ariaLabel: string;
  appearance: ComposerTriggerAppearance;
  disabled?: boolean;
  className?: string;
}

export const ComposerTrigger: FC<ComposerTriggerProps> = ({
  avatarUrl,
  avatarAlt = '',
  placeholder,
  buttonLabel,
  onClick,
  ariaLabel,
  appearance,
  disabled = false,
  className,
}) => {
  const handleClick = useCallback(() => {
    if (disabled) return;
    onClick();
  }, [disabled, onClick]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onClick();
    },
    [disabled, onClick],
  );

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      className={cn(
        'flex h-14 items-center gap-3 rounded-full border px-2 cursor-pointer',
        composerTriggerAppearances[appearance],
        className,
      )}
    >
      <Avatar src={avatarUrl} alt={avatarAlt} className="size-10 shrink-0" />
      <span className="flex-1 text-left text-[15px] font-normal leading-5 tracking-[-0.15px] text-black opacity-50">
        {placeholder}
      </span>
      <PillButton variant="primary" size="md" disabled className="pointer-events-none shrink-0">
        {buttonLabel}
      </PillButton>
    </div>
  );
};
